/* TODO: t() */
/* eslint-disable @typescript-eslint/no-explicit-any */
import ViewState from './ViewState';
import Marker, { MarkerModifier } from './Marker';
import Overlay, { OverlayModifier } from './Overlay';
import diffByKey from '../../../support/helpers/diffByKey';

import leafletStylesheet from 'leaflet/dist/leaflet.css';

import addStylesheet from '../../../support/helpers/addStylesheet';
import autobind from 'class-autobind';
import Leaflet, { Map } from 'leaflet';
import React, { Component, createRef } from 'react';
import shallowEqual from '../../../support/helpers/shallowEqual';
import { FLUSH_VIEWSTATE_DELAY_MS } from './Constants';

import ensureObject from '../../../support/shared/ensureObject';

type ParsedChildren = {
  markerChildren: ObjectOf<ReactElement>;
  overlayChildren: ObjectOf<ReactElement>;
};

type Props = {
  children?: React.ReactNode;
  className?: string;
  viewState: ViewState;
  onViewStateChange: (...args: Array<any>) => any;
  onClick?: ((...args: Array<any>) => any) | null | undefined;
  onZoomReset?: (...args: Array<any>) => any;
  width?: number;
  height?: number;
};

Leaflet.Icon.Default.imagePath = '/assets/leaflet/images';

const MAP_OPTIONS = {
  attributionControl: false,
  minZoom: 6,
  maxZoom: 14,
  doubleClickZoom: false,
  scrollWheelZoom: false,
  crs: Leaflet.CRS.Simple,
};

export default class LeafletMap extends Component<Props> {
  _ref = createRef<HTMLDivElement>();
  _map: Map | null | undefined = null;
  _markers: {
    [key: string]: MarkerModifier;
  } = {};
  _overlays: {
    [key: string]: OverlayModifier;
  } = {};
  // the current state of Leaflet's view (may be temporarily out of sync with props)
  _currentViewState: ViewState | null | undefined = null;
  // a refresh will be scheduled using setTimeout when Leaflet's view is out of sync with props
  _refreshTimeout: TimeoutID | null | undefined = null;
  // an optional button added to the zoom control to reset zoom to default
  _zoomResetButton: Element | null | undefined;

  constructor(props: Props) {
    super(props);
    autobind(this);
  }

  componentDidMount() {
    addStylesheet(leafletStylesheet);
    let { props } = this;
    this._initMap(props);
    let { markerChildren, overlayChildren } = parseChildren(props.children);
    for (let [key, value] of Object.entries(overlayChildren)) {
      this._addOverlay(key, value);
    }
    for (let [key, value] of Object.entries(markerChildren)) {
      this._addMarker(key, value);
    }
    if (props.onZoomReset != null) {
      this._addZoomResetButton();
    }
  }

  componentWillUnmount() {
    if (this._refreshTimeout != null) {
      clearTimeout(this._refreshTimeout);
    }
    this._removeAllMarkers();
    this._removeAllOverlays();
    if (this._map != null) {
      this._map.remove();
    }
  }

  shouldComponentUpdate(): boolean {
    return false;
  }

  // TODO: Refactor this so that it's not using a deprecated lifecycle method.
  componentWillReceiveProps(newProps: Props) {
    let oldProps = this.props;
    let shouldRefreshView = false;
    if (oldProps.children !== newProps.children) {
      shouldRefreshView = true;
    }
    if (
      oldProps.width !== newProps.width ||
      oldProps.height !== newProps.height
    ) {
      this._setContainerSize(newProps);
      shouldRefreshView = true;
    }
    if (
      oldProps.viewState !== newProps.viewState &&
      newProps.viewState !== this._currentViewState
    ) {
      shouldRefreshView = true;
    }
    if (shouldRefreshView) {
      this._refreshView(newProps);
    }
    let oldHasZoomResetButton = oldProps.onZoomReset != null;
    let newHasZoomResetButton = newProps.onZoomReset != null;
    if (oldHasZoomResetButton !== newHasZoomResetButton) {
      newHasZoomResetButton
        ? this._addZoomResetButton()
        : this._removeZoomResetButton();
    }
  }

  render() {
    let { className, width, height } = this.props;
    let style = {
      width,
      height,
    };
    return <div ref={this._ref} className={className} style={style} />;
  }

  _initMap(props: Props) {
    if (this._ref.current) {
      let rootEl = this._ref.current;
      // TODO: options passed as props
      let map = Leaflet.map(rootEl, MAP_OPTIONS);
      this._map = map;
      this._setViewState(props.viewState);
      // add some event handlers
      map.on('click', this._onMapClick);
      // TODO: debounce these due to animation calling moveend excessively?
      map.on('moveend', this._onMapZoomPan);
      map.on('zoomend', this._onMapZoomPan);
    }
  }

  _addZoomResetButton() {
    // TODO: zoomResetText, zoomResetTitle ?
    if (this._map == null) {
      return;
    }
    let zoomControl = this._map.zoomControl;
    let a = (this._zoomResetButton = document.createElement('a'));
    a.className = 'leaflet-control-zoom-reset';
    a.href = '#';
    a.title = 'Reset zoom';
    a.appendChild(document.createTextNode('×'));
    a.addEventListener('mousedown', Leaflet.DomEvent.stopPropagation);
    a.addEventListener('dblclick', Leaflet.DomEvent.stopPropagation);
    a.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (this.props.onZoomReset) {
        this.props.onZoomReset(event);
      }
    });
    // eslint-disable-next-line no-underscore-dangle
    let zoomOutButton = zoomControl._zoomOutButton;
    if (zoomOutButton.parentNode != null) {
      zoomOutButton.parentNode.insertBefore(a, zoomOutButton);
    }
  }

  _removeZoomResetButton() {
    if (this._zoomResetButton != null) {
      if (this._zoomResetButton.parentNode != null) {
        this._zoomResetButton.parentNode.removeChild(this._zoomResetButton);
      }
      this._zoomResetButton = null;
    }
  }

  _refreshView(props?: Props) {
    props = props || this.props;
    this._setViewState(props.viewState);
    let { markerChildren, overlayChildren } = parseChildren(props.children);
    this._updateOverlays(overlayChildren);
    this._updateMarkers(markerChildren);
  }

  _setViewState(viewState: ViewState) {
    let map = this._map;
    if (map != null) {
      let { lat, lng, zoomLevel } = viewState;
      map.setView([lat, lng], zoomLevel, { reset: true });
      this._currentViewState = viewState;
    }
  }

  _onMapZoomPan() {
    let map = this._map;
    if (map == null) {
      return;
    }
    let { lat, lng } = map.getCenter();
    let zoomLevel = map.getZoom();
    let oldViewState = this._currentViewState;
    let newViewState = new ViewState({
      lat: lat,
      lng: lng,
      zoomLevel: zoomLevel,
    });
    if (oldViewState) {
      if (shallowEqual(oldViewState, newViewState)) {
        return;
      }
    }
    this._currentViewState = newViewState;
    // Leaflet view state may now be out of sync with `props.viewState`. Refresh
    // Leaflet's view in a moment (unless we receive newViewState back
    // immediately in the props).
    if (this._refreshTimeout != null) {
      clearTimeout(this._refreshTimeout);
    }
    this._refreshTimeout = setTimeout(() => {
      if (this.props.viewState !== newViewState) {
        this._refreshView();
      }
    }, FLUSH_VIEWSTATE_DELAY_MS);
    this.props.onViewStateChange(newViewState);
  }

  // TODO: Allow auto-sizing.
  _setContainerSize(props: Props) {
    if (this._ref.current) {
      let rootEl = this._ref.current;
      rootEl.style.width = props.width == null ? 'auto' : props.width + 'px';
      rootEl.style.height = props.height == null ? 'auto' : props.height + 'px';
      if (this._map != null) {
        this._map.invalidateSize(true);
      }
    }
  }

  _updateMarkers(markerChildren: { [key: string]: ReactElement }) {
    let { toRemove, toAdd, toUpdate } = diffByKey(
      this._markers,
      markerChildren,
    );
    toRemove.forEach((key) => this._removeMarker(key));
    toUpdate.forEach((key) =>
      this._markers[key]?.update(markerChildren[key] as any),
    );
    toAdd.forEach((key) => this._addMarker(key, markerChildren[key] as any));
  }

  _addMarker(key: string, marker: ReactElement) {
    if (this._map != null) {
      this._markers[key] = Marker.addToMap(marker, this._map);
    }
  }

  _removeMarker(key: string) {
    this._markers[key]?.destroy();
    delete this._markers[key];
  }

  _removeAllMarkers() {
    Object.keys(this._markers).forEach((key) => this._removeMarker(key));
  }

  _updateOverlays(overlayChildren: { [key: string]: ReactElement }) {
    let { toRemove, toAdd, toUpdate } = diffByKey(
      this._overlays,
      overlayChildren,
    );
    toRemove.forEach((key) => this._removeOverlay(key));
    toUpdate.forEach((key) =>
      this._overlays[key]?.update(overlayChildren[key] as any),
    );
    toAdd.forEach((key) => this._addOverlay(key, overlayChildren[key] as any));
  }

  _addOverlay(key: string, overlay: ReactElement) {
    if (this._map != null) {
      this._overlays[key] = Overlay.addToMap(overlay, this._map);
    }
  }

  _removeOverlay(key: string) {
    this._overlays[key]?.destroy();
    delete this._overlays[key];
  }

  _removeAllOverlays() {
    Object.keys(this._overlays).forEach((key) => this._removeOverlay(key));
  }

  _onMapClick(event: { [key: string]: any }) {
    if (this.props.onClick) {
      let { lat, lng } = event.latlng;
      this.props.onClick(lat, lng);
    }
  }
}

function parseChildren(
  children: React.ReactNode | null | undefined,
): ParsedChildren {
  let markerChildren: ObjectOf<ReactElement> = {};
  let overlayChildren: ObjectOf<ReactElement> = {};
  React.Children.forEach(children, (childNode) => {
    let child = ensureObject(childNode);
    if (!child) {
      return;
    }
    if (child.type === Marker) {
      markerChildren[String(child.key)] = child as any;
    } else if (child.type === Overlay) {
      overlayChildren[String(child.key)] = child as any;
    } else {
      throw new Error('LeafletMap children must be of type Marker or Overlay');
    }
  });
  return { markerChildren, overlayChildren };
}
